import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ELayoutManagerMode, routes } from '#shared';

import { useToast } from '../../LayoutManager';
import { useAppSelector, type RootState } from '../../redux-store';
import {
  templateSelectors,
  useApplyTemplate,
  useClearPreview,
  useIsApplying,
  usePreviewTemplate,
  usePreviewTemplateId,
} from '../../slices';
import { LayoutEditingProvider } from '../model';

import { PreviewContainer } from './PreviewContainer';
import { Templates } from './Templates';
import { WidgetPalette } from './WidgetPalette';

const PREFIX = 'TSMED-LayoutSettings';

export const layoutSettingsClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  headerRow: `${PREFIX}-headerRow`,
  title: `${PREFIX}-title`,
  backButton: `${PREFIX}-backButton`,
  applyButton: `${PREFIX}-applyButton`,
} as const;

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
})(({ theme: { spacing, utils, palette } }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  backgroundColor: palette.mapBlue[900],
  padding: spacing(2),
  overflow: 'hidden',
  container: 'layout-settings / size',

  [`& .${layoutSettingsClasses.header}`]: {
    display: 'flex',
    width: '100%',
    maxWidth: '1200px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: spacing(1.5),
  },

  [`& .${layoutSettingsClasses.headerRow}`]: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  [`& .${layoutSettingsClasses.backButton}`]: {
    color: palette.common.white,
    opacity: 1,
    backgroundColor: 'transparent',
    height: 'fit-content',
    fontSize: utils.clamp(14, 16),
    padding: 0,

    svg: {
      fill: 'white',
    },

    '&:hover': {
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    '&:disabled': {
      opacity: 0.4,
      color: palette.grey[500],
      svg: {
        fill: palette.grey[500],
      },
    },
    textTransform: 'none',
    gap: spacing(0.5),
    alignItems: 'center',
  },

  [`& .${layoutSettingsClasses.applyButton}`]: {
    backgroundColor: `${palette.mapBlue[500]}1A`,
    color: palette.mapBlue[300],
    height: 'fit-content',
    fontSize: utils.clamp(14, 16),
    padding: spacing(1, 3),
    borderColor: palette.mapBlue[300],
    borderRadius: spacing(0.5),

    '&:hover': {
      backgroundColor: `${palette.mapBlue[300]}3A`,
    },
    '&:disabled': {
      backgroundColor: palette.blue[800],
      opacity: 0.6,
      color: palette.grey[400],
    },
    textTransform: 'none',
  },

  [`& .${layoutSettingsClasses.title}`]: {
    fontSize: utils.clamp(24, 32),
  },
}));

export const LayoutSettings = () => {
  const navigate = useNavigate();
  const isApplying = useIsApplying();
  const activeTemplateId = useAppSelector(
    templateSelectors.selectActiveTemplateId,
  );
  const activeTemplateIsCustom = useAppSelector(
    templateSelectors.selectActiveTemplateIsCustom,
  );
  const previewTemplateId = usePreviewTemplateId();
  const previewTemplateAction = usePreviewTemplate();
  const templates = useAppSelector((state: RootState) =>
    templateSelectors.selectAll(state),
  );

  // Set previewTemplateId on mount to ensure browser back navigation
  // can properly discard any changes made during editing
  useEffect(() => {
    if (!previewTemplateId && activeTemplateId) {
      previewTemplateAction(activeTemplateId);
    }
    // Only run on mount - we don't want to reset preview when user selects different template
  }, [activeTemplateId, previewTemplateAction, previewTemplateId]);

  // Determine if current template is custom (preview or active)
  const previewTemplate = templates.find(t => t.id === previewTemplateId);
  const isCustom = previewTemplate?.isCustom ?? activeTemplateIsCustom;
  const applyTemplateAction = useApplyTemplate();
  const clearPreview = useClearPreview();

  const { showToast, ToastComponent } = useToast();

  const handleDeleteLastWidget = () => {
    showToast('Removing last widget is not allowed', 'error');
  };

  const handleApplyAndBack = async () => {
    if (previewTemplateId) {
      try {
        await applyTemplateAction(previewTemplateId);
        navigate(routes.MAIN_MAP);
      } catch {
        showToast('Failed to apply template', 'error', {
          vertical: 'top',
          horizontal: 'center',
        });
      }
    } else {
      // No preview template selected, just navigate back
      navigate(routes.MAIN_MAP);
    }
  };

  const handleBack = () => {
    // Just clear preview state and navigate back
    clearPreview();
    navigate(routes.MAIN_MAP);
  };

  return (
    <LayoutEditingProvider
      mode={isCustom ? ELayoutManagerMode.EDIT : ELayoutManagerMode.VIEW}
      onDeleteLastWidget={handleDeleteLastWidget}
    >
      <Root>
        <Box className={layoutSettingsClasses.header}>
          <Box className={layoutSettingsClasses.headerRow}>
            <Button
              variant="text"
              className={layoutSettingsClasses.backButton}
              onClick={handleBack}
              disabled={isApplying}
            >
              <ChevronLeftIcon />
              <Typography>NOP Command</Typography>
            </Button>

            <Button
              variant="outlined"
              className={layoutSettingsClasses.applyButton}
              onClick={handleApplyAndBack}
              disabled={isApplying}
              startIcon={
                isApplying ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isApplying ? 'Applying...' : 'Apply & Back'}
            </Button>
          </Box>

          <Typography className={layoutSettingsClasses.title}>
            Layout Settings
          </Typography>

          <Templates />
        </Box>

        <PreviewContainer />

        <WidgetPalette />

        <ToastComponent />
      </Root>
    </LayoutEditingProvider>
  );
};
